import React, { useState, useEffect, useCallback } from "react";
import { Wallet, Save, Loader, ArrowLeft } from "lucide-react";
import { useAuth } from "./AuthContext";

const BACKEND_URL = process.env.REACT_APP_API_BASE_URL;

const FIELDS = [
  { key: "name", label: "氏名", placeholder: "山田 太郎" },
  { key: "furigana", label: "フリガナ", placeholder: "ヤマダ タロウ" },
  { key: "postal_code", label: "郵便番号", placeholder: "123-4567" },
  { key: "address", label: "住所", placeholder: "東京都渋谷区..." },
  { key: "phone", label: "電話番号", placeholder: "090-1234-5678" },
  { key: "trade_name", label: "屋号", placeholder: "○○商店" },
  { key: "business_type", label: "事業内容", placeholder: "小売業、コンサルティング等" },
  { key: "tax_office", label: "所轄税務署", placeholder: "渋谷税務署" },
  { key: "seiri_number", label: "整理番号", placeholder: "" },
];

const ProfilePage = ({ onBack }) => {
  const { getToken } = useAuth();
  const [profile, setProfile] = useState({
    name: "", furigana: "", postal_code: "", address: "", phone: "",
    trade_name: "", business_type: "", filing_type: "white",
    tax_office: "", seiri_number: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  const authFetch = useCallback(async (url, options = {}) => {
    const token = await getToken();
    const headers = { ...(options.headers || {}), Authorization: `Bearer ${token}` };
    return fetch(url, { ...options, headers });
  }, [getToken]);

  useEffect(() => {
    (async () => {
      try {
        const res = await authFetch(`${BACKEND_URL}/api/profile`);
        const json = await res.json();
        if (json.success && json.data) {
          setProfile((prev) => ({ ...prev, ...json.data }));
        }
      } catch (e) {
        console.error("プロフィール取得エラー:", e);
      } finally {
        setLoading(false);
      }
    })();
  }, [authFetch]);

  const searchPostalCode = async () => {
    const code = (profile.postal_code || "").replace("-", "");
    if (code.length !== 7) {
      alert("郵便番号は7桁で入力してください");
      return;
    }
    try {
      const res = await fetch(`https://zipcloud.ibsnet.co.jp/api/search?zipcode=${code}`);
      const json = await res.json();
      if (json.results && json.results.length > 0) {
        const r = json.results[0];
        const addr = r.address1 + r.address2 + r.address3;
        setProfile((prev) => ({ ...prev, address: addr }));
      } else {
        alert("該当する住所が見つかりませんでした");
      }
    } catch (e) {
      console.error("郵便番号検索エラー:", e);
      alert("郵便番号検索に失敗しました");
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage("");
    try {
      const res = await authFetch(`${BACKEND_URL}/api/profile`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(profile),
      });
      const json = await res.json();
      if (json.success) {
        setMessage("保存しました");
        setTimeout(() => setMessage(""), 3000);
      } else {
        setMessage("保存に失敗しました");
      }
    } catch (e) {
      console.error("保存エラー:", e);
      setMessage("保存に失敗しました");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-page flex items-center justify-center">
        <Loader size={24} className="animate-spin text-muted" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-page text-color font-sans">
      <header className="sticky top-0 z-50 backdrop-blur-xl border-b border-theme"
        style={{ backgroundColor: "var(--color-bg)", opacity: 0.97 }}>
        <div className="max-w-[800px] mx-auto px-5 py-3.5 flex justify-between items-center">
          <div className="flex items-center gap-2.5">
            <div className="p-2 rounded-xl flex items-center justify-center"
              style={{ background: "linear-gradient(135deg, var(--color-accent), var(--color-accent-soft))" }}>
              <Wallet size={22} color="#fff" />
            </div>
            <span className="text-xl font-bold tracking-tight text-color">基本情報</span>
          </div>
          <button onClick={onBack}
            className="flex items-center gap-1.5 text-sm text-accent hover:opacity-80 bg-transparent border-none cursor-pointer">
            <ArrowLeft size={16} /> 戻る
          </button>
        </div>
      </header>

      <main className="max-w-[800px] mx-auto px-5 py-7">
        <div className="card">
          <h3 className="text-lg font-bold mb-6">事業者情報</h3>

          {FIELDS.map((field) => (
            <div key={field.key} className="mb-4">
              <label className="field-label">{field.label}</label>
              {field.key === "postal_code" ? (
                <div>
                  <input
                    type="text"
                    value={profile.postal_code || ""}
                    placeholder={field.placeholder}
                    onChange={(e) => setProfile((prev) => ({ ...prev, postal_code: e.target.value }))}
                    className="input-field mb-2"
                  />
                  <button onClick={searchPostalCode} type="button"
                    className="btn-primary text-sm"
                    style={{ width: "100%", padding: "10px" }}>
                    住所を検索
                  </button>
                </div>
              ) : (
                <input
                  type="text"
                  value={profile[field.key] || ""}
                  placeholder={field.placeholder}
                  onChange={(e) => setProfile((prev) => ({ ...prev, [field.key]: e.target.value }))}
                  className="input-field"
                />
              )}
            </div>
          ))}

          <div className="mb-6">
            <label className="field-label">申告区分</label>
            <select
              value={profile.filing_type}
              onChange={(e) => setProfile((prev) => ({ ...prev, filing_type: e.target.value }))}
              className="select-field"
            >
              <option value="white">白色申告</option>
              <option value="blue">青色申告</option>
            </select>
          </div>

          {message && (
            <div className={`text-sm mb-4 ${message === "保存しました" ? "text-income" : "text-expense"}`}>
              {message}
            </div>
          )}

          <button onClick={handleSave} disabled={saving} className="btn-primary flex items-center justify-center gap-2">
            {saving ? <Loader size={16} className="animate-spin" /> : <Save size={16} />}
            {saving ? "保存中..." : "保存する"}
          </button>
        </div>
      </main>
    </div>
  );
};

export default ProfilePage;